import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Flex,
  FormControl,
  Heading,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  VStack,
  keyframes,
  useBreakpointValue,
} from '@chakra-ui/react';
import { FaBuilding, FaCheck, FaEnvelope, FaMapMarkerAlt, FaStar } from 'react-icons/fa';
import {
  previewMarketCompetitors,
  resolveProspectMarketKeyword,
} from '../services/landingMarketService';

const fadeInStep = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const orbitSpin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const ANALYSIS_STEPS = [
  {
    label: 'Locking your Google Business Profile',
    sub: 'Confirming the exact listing we will analyze',
    duration: 2200,
  },
  {
    label: 'Testing local market keywords',
    sub: 'City-wide and neighborhood searches until you appear',
    duration: 3600,
  },
  {
    label: 'Loading Competitors Data',
    sub: 'Pulling the businesses Google shows for this search',
    duration: 2800,
  },
  {
    label: 'Scoring competitor match quality',
    sub: 'Reviews, category fit, and local relevance',
    duration: 2400,
  },
  {
    label: 'Preparing your competitive report',
    sub: 'Packaging insights for your PDF',
    duration: 2200,
  },
];

const COMPETITORS_STEP_INDEX = ANALYSIS_STEPS.findIndex(
  (step) => step.label === 'Loading Competitors Data',
);

// The progress bar climbs to this ceiling on the timed animation but never
// crosses it until real competitor data has loaded. This guarantees the load
// screen (and its competitor panel) stays on screen until there is something
// real to show, instead of racing to 100% while only skeletons are visible.
const PROGRESS_HOLD_CEILING = 90;

// Once competitor data has landed, ramp from wherever we are up to 100% over
// this window so the freshly loaded competitor rows are actually visible before
// the modal flips to the completion view.
const PROGRESS_REVEAL_MS = 1800;

function isSameBusiness(anchor, competitor) {
  const anchorId = (anchor?.place_id || '').replace(/^places\//, '');
  const competitorId = (competitor?.place_id || '').replace(/^places\//, '');
  if (anchorId && competitorId && anchorId === competitorId) return true;
  return (
    (anchor?.name || '').trim().toLowerCase() ===
    (competitor?.name || '').trim().toLowerCase()
  );
}

function CompetitorRow({ competitor, isProspect, index }) {
  return (
    <Box
      px={3}
      py={2.5}
      borderRadius="lg"
      bg={isProspect ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.72)'}
      border="1px solid"
      borderColor={isProspect ? 'blue.300' : 'rgba(147, 197, 253, 0.55)'}
      backdropFilter="blur(8px)"
      animation={`${fadeInStep} 0.35s ease-out`}
      style={{ animationDelay: `${Math.min(index, 8) * 40}ms`, animationFillMode: 'both' }}
      transition="border-color 0.15s ease, background 0.15s ease"
      _hover={{
        bg: 'white',
        borderColor: 'blue.300',
      }}
    >
      <HStack justify="space-between" align="center" spacing={3}>
        <HStack spacing={2.5} flex={1} minW={0} align="center">
          <Flex
            w="28px"
            h="28px"
            borderRadius="md"
            justify="center"
            align="center"
            flexShrink={0}
            bg={isProspect ? 'blue.500' : 'blue.100'}
            color={isProspect ? 'white' : 'blue.600'}
            fontSize="xs"
            fontWeight="700"
          >
            {isProspect ? (
              <Icon as={FaBuilding} boxSize={3} />
            ) : (
              index + 1
            )}
          </Flex>
          <Box flex={1} minW={0}>
            <HStack spacing={2} align="center">
              <Text fontSize="sm" fontWeight="600" color="gray.800" noOfLines={1}>
                {competitor.name}
              </Text>
              {isProspect && (
                <Box
                  px={1.5}
                  py={0.5}
                  borderRadius="full"
                  bg="blue.500"
                  color="white"
                  fontSize="9px"
                  fontWeight="700"
                  letterSpacing="0.04em"
                  flexShrink={0}
                  textTransform="uppercase"
                >
                  You
                </Box>
              )}
            </HStack>
            {competitor.address && (
              <HStack spacing={1} mt={0.5} align="center">
                <Flex w="12px" h="12px" justify="center" align="center" flexShrink={0}>
                  <Icon as={FaMapMarkerAlt} boxSize={2.5} color="blue.300" />
                </Flex>
                <Text fontSize="xs" color="blue.700" opacity={0.7} noOfLines={1}>
                  {competitor.address}
                </Text>
              </HStack>
            )}
          </Box>
        </HStack>
        {competitor.rating != null && (
          <HStack spacing={0.5} flexShrink={0}>
            <Icon as={FaStar} boxSize={3} color="orange.400" />
            <Text fontSize="xs" fontWeight="700" color="gray.700">
              {competitor.rating.toFixed(1)}
            </Text>
          </HStack>
        )}
      </HStack>
    </Box>
  );
}

function CompetitorSkeleton({ index }) {
  return (
    <Box
      px={3}
      py={3}
      borderRadius="lg"
      bg="rgba(255, 255, 255, 0.45)"
      border="1px solid"
      borderColor="rgba(147, 197, 253, 0.4)"
    >
      <HStack spacing={2.5}>
        <Box
          w="28px"
          h="28px"
          borderRadius="md"
          bg="blue.100"
          opacity={0.7}
        />
        <VStack align="stretch" spacing={1.5} flex={1}>
          <Box
            h="10px"
            w={`${62 - (index % 3) * 10}%`}
            borderRadius="full"
            bg="linear-gradient(90deg, #bfdbfe 0%, #e0f2fe 50%, #bfdbfe 100%)"
            backgroundSize="200% 100%"
            animation={`${shimmer} 1.6s ease-in-out infinite`}
          />
          <Box
            h="8px"
            w={`${48 - (index % 2) * 8}%`}
            borderRadius="full"
            bg="linear-gradient(90deg, #dbeafe 0%, #f0f9ff 50%, #dbeafe 100%)"
            backgroundSize="200% 100%"
            animation={`${shimmer} 1.6s ease-in-out infinite`}
            style={{ animationDelay: '0.15s' }}
          />
        </VStack>
      </HStack>
    </Box>
  );
}

function CompetitorsPanel({ competitors, anchorBusiness, marketKeyword, isLoading }) {
  return (
    <Box
      flex={{ base: '1 1 auto', md: '1 1 52%' }}
      minW={0}
      h={{ base: 'auto', md: '100%' }}
      borderRadius="2xl"
      bg="linear-gradient(165deg, #eff6ff 0%, #dbeafe 48%, #e0f2fe 100%)"
      border="1px solid"
      borderColor="blue.200"
      p={{ base: 3, md: 4 }}
      display="flex"
      flexDirection="column"
      minH={{ base: '220px', md: '360px' }}
      maxH={{ base: '280px', md: '420px' }}
      animation={`${fadeInStep} 0.45s ease-out`}
    >
      <HStack justify="space-between" align="center" mb={3} px={0.5}>
        <Box>
          <Text
            fontSize="xs"
            fontWeight="700"
            color="blue.600"
            textTransform="uppercase"
            letterSpacing="0.08em"
          >
            Competitors
          </Text>
          <Text fontSize="sm" fontWeight="600" color="gray.700" mt={0.5}>
            {isLoading
              ? 'Mapping your local market…'
              : `${competitors.length} found${marketKeyword ? ` · ${marketKeyword}` : ''}`}
          </Text>
        </Box>
        {!isLoading && competitors.length > 0 && (
          <Flex
            px={2.5}
            py={1}
            borderRadius="full"
            bg="white"
            border="1px solid"
            borderColor="blue.200"
            color="blue.600"
            fontSize="xs"
            fontWeight="700"
          >
            Live
          </Flex>
        )}
      </HStack>

      <Box flex={1} overflowY="auto" pr={1} css={{
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(59, 130, 246, 0.35)',
          borderRadius: '4px',
        },
      }}>
        <VStack spacing={2} align="stretch">
          {isLoading
            ? Array.from({ length: 5 }).map((_, index) => (
                <CompetitorSkeleton key={`skeleton-${index}`} index={index} />
              ))
            : competitors.map((competitor, index) => (
                <CompetitorRow
                  key={competitor.place_id || competitor.name}
                  competitor={competitor}
                  isProspect={isSameBusiness(anchorBusiness, competitor)}
                  index={index}
                />
              ))}
        </VStack>
      </Box>
    </Box>
  );
}

function ProspectMarketAnalysisModal({
  isOpen,
  onClose,
  anchorBusiness,
  marketKeyword,
  competitors,
  analysisProgress,
  analysisStep,
  email,
  onEmailChange,
  emailError = '',
  onConfirm,
  isConfirming,
  confirmLabel = 'Email Me the PDF',
  confirmHelpText = "We'll email your PDF report as soon as the market scrape finishes. No card required.",
}) {
  const isComplete = analysisProgress >= 100;
  const isDesktop = useBreakpointValue({ base: false, md: true });
  const canSubmit = Boolean(email?.trim() && email.includes('@'));
  const competitorsReady = competitors.length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={isComplete ? onClose : () => {}}
      isCentered
      size={{ base: 'md', md: isComplete ? 'lg' : '4xl' }}
      motionPreset="slideInBottom"
      closeOnOverlayClick={isComplete}
    >
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(10px)" />
      <ModalContent
        borderRadius="2xl"
        mx={4}
        overflow="hidden"
        bg="white"
        shadow="2xl"
        maxW={isComplete ? undefined : { md: '920px' }}
      >
        <ModalBody p={0}>
          <Box
            bg="linear-gradient(160deg, #f8faff 0%, #eef2ff 40%, #f0f7ff 100%)"
            position="relative"
            overflow="hidden"
          >
            <Box
              position="absolute"
              inset={0}
              opacity={0.35}
              background="linear-gradient(90deg, transparent 0%, rgba(0,102,255,0.04) 50%, transparent 100%)"
              backgroundSize="200% 100%"
              animation={`${shimmer} 3s ease-in-out infinite`}
              pointerEvents="none"
            />

            <Box position="relative" py={{ base: 6, md: 8 }} px={{ base: 4, md: 6 }}>
              {!isComplete ? (
                <Flex
                  direction={{ base: 'column', md: 'row' }}
                  gap={{ base: 5, md: 6 }}
                  align={{ base: 'stretch', md: 'stretch' }}
                >
                  {/* Left: percent + steps */}
                  <VStack
                    spacing={5}
                    flex={{ base: '1 1 auto', md: '0 0 42%' }}
                    align="stretch"
                    justify="center"
                  >
                    <VStack spacing={3}>
                      <Box position="relative" borderRadius="full" alignSelf="center">
                        <Box
                          position="absolute"
                          inset="-12px"
                          borderRadius="full"
                          border="2px dashed"
                          borderColor="blue.200"
                          opacity={0.5}
                          animation={`${orbitSpin} 8s linear infinite`}
                        />
                        <CircularProgress
                          value={analysisProgress}
                          size={{ base: '96px', md: isDesktop ? '120px' : '96px' }}
                          thickness="5px"
                          color="blue.500"
                          trackColor="blue.50"
                          capIsRound
                        >
                          <Flex
                            position="absolute"
                            inset={0}
                            align="center"
                            justify="center"
                            pointerEvents="none"
                          >
                            <Text
                              as="span"
                              fontSize={{ base: '2xl', md: '3xl' }}
                              fontWeight="bold"
                              color="gray.800"
                              lineHeight="1"
                              m={0}
                              transform="translateY(3px)"
                            >
                              {Math.round(analysisProgress)}%
                            </Text>
                          </Flex>
                        </CircularProgress>
                      </Box>

                      <VStack spacing={1}>
                        <Heading size="md" color="gray.800" textAlign="center">
                          Analyzing your market
                        </Heading>
                        <Text fontSize="sm" color="gray.500" textAlign="center" noOfLines={1}>
                          {anchorBusiness?.name || 'Your business'}
                        </Text>
                      </VStack>
                    </VStack>

                    <VStack spacing={2.5} w="full" align="stretch">
                      {ANALYSIS_STEPS.map((item, index) => {
                        const isActive = index === analysisStep;
                        const isStepComplete =
                          index < analysisStep ||
                          (index === COMPETITORS_STEP_INDEX && competitorsReady);
                        const isUpcoming = index > analysisStep;

                        return (
                          <Box
                            key={item.label}
                            w="full"
                            opacity={isUpcoming ? 0.35 : 1}
                            animation={isActive ? `${fadeInStep} 0.5s ease-out` : undefined}
                          >
                            <HStack spacing={3}>
                              <Flex
                                w="28px"
                                h="28px"
                                borderRadius="full"
                                align="center"
                                justify="center"
                                flexShrink={0}
                                bg={isStepComplete ? 'green.50' : isActive ? 'blue.50' : 'white'}
                                border="2px solid"
                                borderColor={
                                  isStepComplete
                                    ? 'green.400'
                                    : isActive
                                      ? 'blue.400'
                                      : 'gray.200'
                                }
                              >
                                {isStepComplete ? (
                                  <Icon as={FaCheck} color="green.500" boxSize={3} />
                                ) : (
                                  <Box
                                    w="8px"
                                    h="8px"
                                    borderRadius="full"
                                    bg={isActive ? 'blue.500' : 'gray.300'}
                                  />
                                )}
                              </Flex>
                              <VStack align="start" spacing={0} flex={1} minW={0}>
                                <Text
                                  fontSize="sm"
                                  fontWeight={isActive ? 'semibold' : 'medium'}
                                  color={isActive ? 'gray.800' : 'gray.500'}
                                  noOfLines={1}
                                >
                                  {item.label}
                                </Text>
                                {(isActive || isStepComplete) && item.sub && (
                                  <Text
                                    fontSize="xs"
                                    color={isActive ? 'blue.500' : 'gray.400'}
                                    noOfLines={1}
                                  >
                                    {item.sub}
                                  </Text>
                                )}
                              </VStack>
                            </HStack>
                          </Box>
                        );
                      })}
                    </VStack>
                  </VStack>

                  {/* Right: competitors panel */}
                  <CompetitorsPanel
                    competitors={competitors}
                    anchorBusiness={anchorBusiness}
                    marketKeyword={marketKeyword}
                    isLoading={!competitorsReady}
                  />
                </Flex>
              ) : (
                <VStack spacing={6} align="stretch" maxW="420px" mx="auto">
                  <VStack spacing={3}>
                    <CircularProgress
                      value={100}
                      size="96px"
                      thickness="5px"
                      color="green.400"
                      trackColor="green.50"
                      capIsRound
                    >
                      <Flex
                        position="absolute"
                        inset={0}
                        justify="center"
                        align="center"
                        pointerEvents="none"
                      >
                        <Flex justify="center" align="center" transform="translateY(1px)">
                          <Icon as={FaCheck} color="green.500" boxSize={6} />
                        </Flex>
                      </Flex>
                    </CircularProgress>
                    <Heading size="md" color="gray.800" textAlign="center">
                      Your market analysis is ready
                    </Heading>
                    <Text fontSize="sm" color="gray.500" textAlign="center">
                      {anchorBusiness?.name || 'Your business'}
                    </Text>
                    {competitorsReady && (
                      <Text fontSize="xs" color="blue.600" fontWeight="600" textAlign="center">
                        {competitors.length} competitors mapped
                        {marketKeyword ? ` · ${marketKeyword}` : ''}
                      </Text>
                    )}
                  </VStack>

                  <VStack spacing={3} align="stretch">
                    <FormControl isRequired>
                      <InputGroup size="md">
                        <InputLeftElement h="full">
                          <Icon as={FaEnvelope} color="gray.400" boxSize={4} />
                        </InputLeftElement>
                        <Input
                          type="email"
                          placeholder="Business email for your PDF"
                          value={email}
                          onChange={(e) => onEmailChange?.(e.target.value)}
                          bg="white"
                          borderColor="gray.200"
                          pl="44px"
                          fontSize="16px"
                          _focus={{
                            borderColor: 'blue.400',
                            boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)',
                          }}
                          data-testid="landing-modal-email-input"
                        />
                      </InputGroup>
                    </FormControl>
                    {emailError && (
                      <Text fontSize="sm" color="red.500" textAlign="center">
                        {emailError}
                      </Text>
                    )}
                    <Button
                      size="lg"
                      h="54px"
                      borderRadius="xl"
                      bg="#0066FF"
                      color="white"
                      fontWeight="800"
                      onClick={onConfirm}
                      isLoading={isConfirming}
                      isDisabled={!canSubmit}
                      _hover={{ bg: '#0052CC', transform: 'translateY(-1px)' }}
                      _disabled={{ opacity: 0.55, cursor: 'not-allowed' }}
                      data-testid="landing-email-pdf-cta"
                    >
                      {confirmLabel}
                    </Button>
                    <Text fontSize="xs" color="gray.500" textAlign="center">
                      {confirmHelpText}
                    </Text>
                  </VStack>
                </VStack>
              )}
            </Box>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export function useProspectMarketAnalysis() {
  const [isOpen, setIsOpen] = useState(false);
  const [analysisStep, setAnalysisStep] = useState(0);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [marketResolution, setMarketResolution] = useState(null);
  const [competitors, setCompetitors] = useState([]);
  const [marketKeyword, setMarketKeyword] = useState('');
  const progressTimerRef = useRef(null);
  // Set to true once the competitor data fetch has completed (success or empty).
  const apiDoneRef = useRef(false);
  // Bookkeeping for the "reveal" ramp that runs after data lands.
  const readyAtRef = useRef(null);
  const pctAtReadyRef = useRef(0);
  const lastPctRef = useRef(0);

  const cleanupProgress = useCallback(() => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  }, []);

  useEffect(() => cleanupProgress, [cleanupProgress]);

  const runProgressAnimation = useCallback(
    () =>
      new Promise((resolve) => {
        const totalDuration = ANALYSIS_STEPS.reduce((sum, item) => sum + item.duration, 0);
        const progressInterval = 50;
        let elapsed = 0;

        progressTimerRef.current = setInterval(() => {
          elapsed += progressInterval;
          const timePct = Math.min((elapsed / totalDuration) * 100, 100);

          // Phase 1 — waiting on data: climb the timeline but hold below the
          // ceiling, and park on the "Loading Competitors Data" step so the UI
          // never advertises completion while only skeletons are shown.
          if (!apiDoneRef.current) {
            const pct = Math.min(timePct, PROGRESS_HOLD_CEILING);
            lastPctRef.current = pct;
            setAnalysisProgress(pct);

            let timedStep = 0;
            let cumulative = 0;
            for (let index = 0; index < ANALYSIS_STEPS.length; index += 1) {
              cumulative += ANALYSIS_STEPS[index].duration;
              if (elapsed < cumulative) {
                timedStep = index;
                break;
              }
              timedStep = ANALYSIS_STEPS.length - 1;
            }
            setAnalysisStep(Math.min(timedStep, COMPETITORS_STEP_INDEX));
            return;
          }

          // Phase 2 — data ready: ramp smoothly from wherever we are up to 100%
          // over PROGRESS_REVEAL_MS so the loaded competitor rows are visible.
          if (readyAtRef.current === null) {
            readyAtRef.current = elapsed;
            pctAtReadyRef.current = lastPctRef.current;
          }
          const revealElapsed = elapsed - readyAtRef.current;
          const from = pctAtReadyRef.current;
          const pct = Math.min(
            from + ((100 - from) * revealElapsed) / PROGRESS_REVEAL_MS,
            100,
          );
          lastPctRef.current = pct;
          setAnalysisProgress(pct);
          setAnalysisStep(ANALYSIS_STEPS.length - 1);

          if (pct >= 100) {
            cleanupProgress();
            setAnalysisProgress(100);
            setAnalysisStep(ANALYSIS_STEPS.length - 1);
            resolve();
          }
        }, progressInterval);
      }),
    [cleanupProgress],
  );

  const startAnalysis = useCallback(
    async (anchorBusiness, retryHints = {}) => {
      setIsOpen(true);
      setAnalysisStep(0);
      setAnalysisProgress(0);
      setMarketResolution(null);
      setCompetitors([]);
      setMarketKeyword('');
      apiDoneRef.current = false;
      readyAtRef.current = null;
      pctAtReadyRef.current = 0;
      lastPctRef.current = 0;

      try {
        const apiPromise = (async () => {
          const resolution = await resolveProspectMarketKeyword(anchorBusiness, {
            neighborhood: retryHints.neighborhood || '',
            manualCity: retryHints.manualCity || '',
          });
          if (!resolution?.target_resolved && !resolution?.targetResolved) {
            const notFound = new Error(
              'We could not find this business in the top 20 for any local search we tried.',
            );
            notFound.code = 'MARKET_NOT_FOUND';
            throw notFound;
          }

          const recommended = resolution.recommended;
          const keyword = recommended?.query;
          if (!keyword) {
            throw new Error('Could not resolve a market keyword for this business.');
          }

          setMarketResolution(resolution);
          setMarketKeyword(keyword);

          const preview = await previewMarketCompetitors(
            keyword,
            20,
            resolution.market_label || '',
          );
          setCompetitors(preview.competitors || []);
          // Signal the progress animation that real data has arrived so it can
          // reveal the competitor rows and finish to 100%.
          apiDoneRef.current = true;
          return {
            resolution,
            keyword,
            competitors: preview.competitors || [],
          };
        })();

        await Promise.all([runProgressAnimation(), apiPromise]);
      } catch (error) {
        cleanupProgress();
        setIsOpen(false);
        setAnalysisStep(0);
        setAnalysisProgress(0);
        apiDoneRef.current = false;
        readyAtRef.current = null;
        throw error;
      }
    },
    [cleanupProgress, runProgressAnimation],
  );

  const closeAnalysis = useCallback(() => {
    cleanupProgress();
    setIsOpen(false);
    setAnalysisStep(0);
    setAnalysisProgress(0);
  }, [cleanupProgress]);

  return {
    isOpen,
    analysisStep,
    analysisProgress,
    marketResolution,
    marketKeyword,
    competitors,
    startAnalysis,
    closeAnalysis,
  };
}

export default ProspectMarketAnalysisModal;
